import { useState, useEffect, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/videos';

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  const solid = !transparent || scrolled;

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  const navLink = (to: string, label: string) => {
    const active = to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);
    return (
      <Link
        key={to}
        to={to}
        className={`relative px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
          active ? 'text-accent-500' : 'text-foreground-700 hover:text-foreground-950'
        }`}
      >
        {label}
        {active && <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 rounded-full bg-accent-500" />}
      </Link>
    );
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? 'bg-background-50/95 backdrop-blur border-b border-background-200/70' : 'bg-transparent'
      }`}
    >
      <div className="flex items-center gap-4 h-16 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-500 text-background-50">
            <i className="ri-play-fill text-xl" />
          </span>
          <span className="font-heading text-lg font-extrabold tracking-tight text-foreground-950">
            啪啪<span className="text-accent-500">视频</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-2">
          {navLink('/', '首页')}
          <div className="relative" onMouseEnter={() => setCatOpen(true)} onMouseLeave={() => setCatOpen(false)}>
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname.startsWith('/category')
                  ? 'text-accent-500'
                  : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              分类
              <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? 'rotate-180' : ''}`} />
            </button>
            {catOpen && (
              <div className="absolute left-0 top-full pt-2 w-60">
                <div className="rounded-lg border border-background-200/70 bg-background-100 p-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-200/60 hover:text-foreground-950 transition-colors"
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-500/15 text-accent-500">
                        <i className={`${cat.icon} text-base`} />
                      </span>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {navLink('/search', '搜索')}
          {navLink('/about', '关于')}
        </nav>

        <form onSubmit={submitSearch} className="hidden lg:flex items-center ml-auto">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片、分类或标签"
              className="w-56 xl:w-64 h-10 pl-9 pr-3 text-sm rounded-md bg-background-100 border border-background-200/70 text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-accent-500 transition-colors"
            />
          </div>
        </form>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="ml-auto md:ml-0 lg:ml-3 w-10 h-10 flex items-center justify-center rounded-md text-foreground-900 md:hidden"
          aria-label="展开菜单"
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} />
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50/95 backdrop-blur px-4 py-4 space-y-3">
          <form onSubmit={submitSearch} className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片、分类或标签"
              className="w-full h-10 pl-9 pr-3 text-sm rounded-md bg-background-100 border border-background-200/70 text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-accent-500"
            />
          </form>
          <div className="flex flex-wrap gap-2">
            <Link to="/" className="px-3 py-2 text-sm rounded-md bg-background-100 text-foreground-800">首页</Link>
            <Link to="/search" className="px-3 py-2 text-sm rounded-md bg-background-100 text-foreground-800">搜索</Link>
            <Link to="/about" className="px-3 py-2 text-sm rounded-md bg-background-100 text-foreground-800">关于</Link>
          </div>
          <div>
            <p className="text-xs text-foreground-500 mb-2">视频分类</p>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="px-3 py-2 text-sm rounded-md border border-background-200/70 text-foreground-700"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}